import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { CalendarDays, Wallet, User, CheckCircle2, XCircle, ClipboardCheck, BadgeCheck, Clock, Upload, Image as ImageIcon, X, Camera, TrendingUp, Download, Pencil } from "lucide-react";

const STATUS_COLOR = {
  pending: "bg-amber-100 text-amber-800",
  confirmed: "bg-emerald-100 text-emerald-800",
  completed: "bg-neutral-900 text-white",
  declined: "bg-red-100 text-red-800",
  cancelled: "bg-neutral-200 text-neutral-700",
  reschedule_pending: "bg-amber-100 text-amber-800",
  refunded: "bg-neutral-200 text-neutral-700",
};

export default function TrainerDashboard() {
  const qc = useQueryClient();
  const [tab, setTab] = useState("bookings");
  const me = useQuery({ queryKey: ["trainer-me"], queryFn: async () => (await api.get("/trainer/me")).data });
  const bookings = useQuery({ queryKey: ["trainer-bookings"], queryFn: async () => (await api.get("/trainer/bookings")).data });
  const stats = useQuery({ queryKey: ["trainer-stats"], queryFn: async () => (await api.get("/trainer/stats")).data });

  const action = useMutation({
    mutationFn: ({ id, act }) => api.post(`/trainer/bookings/${id}/${act}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["trainer-bookings"] }); qc.invalidateQueries({ queryKey: ["trainer-stats"] }); toast.success("Updated"); },
  });

  const [form, setForm] = useState(null);
  useEffect(() => { if (me.data) setForm(me.data); }, [me.data]);

  const uploadProfile = useMutation({
    mutationFn: async (file) => {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/trainer/upload/profile", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data;
    },
    onSuccess: (data) => {
      toast.success("Profile photo updated");
      setForm((f) => ({ ...f, photo_url: data.photo_url }));
      qc.invalidateQueries({ queryKey: ["trainer-me"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Upload failed"),
  });

  const uploadGallery = useMutation({
    mutationFn: async (file) => {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/trainer/upload/gallery", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data;
    },
    onSuccess: (data) => {
      toast.success("Photo added to gallery");
      setForm((f) => ({ ...f, gallery: [...(f?.gallery || []), data.url] }));
      qc.invalidateQueries({ queryKey: ["trainer-me"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Upload failed"),
  });

  const deleteGallery = useMutation({
    mutationFn: (path) => api.post("/trainer/gallery/delete", { path }),
    onSuccess: (_r, path) => {
      toast.success("Photo removed");
      setForm((f) => ({ ...f, gallery: (f?.gallery || []).filter((p) => p !== path) }));
      qc.invalidateQueries({ queryKey: ["trainer-me"] });
    },
  });

  const saveProfile = useMutation({
    mutationFn: () => api.put("/trainer/me", form),
    onSuccess: () => { toast.success("Profile saved"); qc.invalidateQueries({ queryKey: ["trainer-me"] }); },
  });

  return (
    <div className="container-fl py-10">
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <div className="overline mb-2">Trainer</div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tighter">{me.data?.name || "Dashboard"}</h1>
          <div className="mt-2">
            <span className={`text-xs font-bold uppercase tracking-widest rounded-full px-2.5 py-1 ${me.data?.status === "approved" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
              {me.data?.status === "approved" ? <><BadgeCheck size={12} className="inline mr-1"/>Approved</> : <><Clock size={12} className="inline mr-1"/>{me.data?.status}</>}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Stat label="Your earnings" value={`€${(stats.data?.net_earnings || 0).toFixed(2)}`} icon={<Wallet size={16}/>} accent/>
        <Stat label="Total bookings" value={(bookings.data || []).length} icon={<CalendarDays size={16}/>}/>
        <Stat label="Paid bookings" value={stats.data?.paid_bookings || 0} icon={<CheckCircle2 size={16}/>}/>
        <Stat label="Rating" value={me.data?.rating_avg?.toFixed(1) || "—"} icon={<ClipboardCheck size={16}/>}/>
      </div>

      <div className="flex gap-2 mb-6">
        <button data-testid="trainer-tab-bookings" onClick={() => setTab("bookings")} className={`pill ${tab === "bookings" ? "pill-active" : ""}`}><CalendarDays size={14}/> Bookings</button>
        <button data-testid="trainer-tab-earnings" onClick={() => setTab("earnings")} className={`pill ${tab === "earnings" ? "pill-active" : ""}`}><TrendingUp size={14}/> Earnings</button>
        <button data-testid="trainer-tab-profile" onClick={() => setTab("profile")} className={`pill ${tab === "profile" ? "pill-active" : ""}`}><User size={14}/> Profile</button>
      </div>

      {tab === "bookings" && (
        <div data-testid="trainer-bookings-list" className="space-y-3">
          {(bookings.data || []).length === 0 ? (
            <div className="border border-dashed border-neutral-200 rounded-xl p-10 text-center text-neutral-500">No bookings yet.</div>
          ) : (bookings.data || []).map((b) => (
            <div key={b.id} className="card-fl p-5 flex items-center justify-between gap-4">
              <div>
                <div className="text-xs text-neutral-500">{b.date} · {b.slot}</div>
                <div className="font-bold">{b.service_title}</div>
                <div className="text-sm text-neutral-500">Client: {b.customer_name} · €{b.price}</div>
                {b.notes && <div className="text-xs text-neutral-500 mt-1">Notes: {b.notes}</div>}
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs font-bold uppercase tracking-widest rounded-full px-2.5 py-1 ${STATUS_COLOR[b.status] || "bg-neutral-100"}`}>{b.status.replace("_"," ")}</span>
                {b.status === "pending" && (
                  <>
                    <button data-testid={`accept-${b.id}`} onClick={() => action.mutate({ id: b.id, act: "accept" })} className="btn-accent !py-1.5 !px-3 !text-xs"><CheckCircle2 size={12}/> Accept</button>
                    <button data-testid={`decline-${b.id}`} onClick={() => action.mutate({ id: b.id, act: "decline" })} className="btn-secondary !py-1.5 !px-3 !text-xs"><XCircle size={12}/> Decline</button>
                  </>
                )}
                {b.status === "reschedule_pending" && (
                  <>
                    <span className="text-xs text-neutral-500">→ {b.reschedule_request?.date} {b.reschedule_request?.slot}</span>
                    <button data-testid={`resched-accept-${b.id}`} onClick={() => action.mutate({ id: b.id, act: "reschedule/accept" })} className="btn-accent !py-1.5 !px-3 !text-xs"><CheckCircle2 size={12}/> Accept</button>
                    <button data-testid={`resched-decline-${b.id}`} onClick={() => action.mutate({ id: b.id, act: "reschedule/decline" })} className="btn-secondary !py-1.5 !px-3 !text-xs"><XCircle size={12}/> Keep original</button>
                  </>
                )}
                {b.status === "confirmed" && (
                  <button data-testid={`complete-${b.id}`} onClick={() => action.mutate({ id: b.id, act: "complete" })} className="btn-primary !py-1.5 !px-3 !text-xs">Mark complete</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "earnings" && (
        <EarningsTab statsData={stats.data} />
      )}

      {tab === "profile" && form && (
        <div className="space-y-8 max-w-3xl">
          {/* Profile photo uploader */}
          <div className="card-fl p-6">
            <div className="flex items-center gap-2 mb-4 font-bold"><Camera size={16}/> Profile photo</div>
            <div className="flex items-center gap-5">
              <div className="w-24 h-24 rounded-full bg-neutral-100 overflow-hidden ring-2 ring-neutral-200">
                {form.photo_url
                  ? <img src={form.photo_url.startsWith("http") ? form.photo_url : `${process.env.REACT_APP_BACKEND_URL}${form.photo_url}`} alt="Profile" className="w-full h-full object-cover"/>
                  : <div className="w-full h-full grid place-items-center text-neutral-400 text-xs">No photo</div>}
              </div>
              <div>
                <label className="btn-primary cursor-pointer" data-testid="upload-profile-btn">
                  <Upload size={14}/> {uploadProfile.isPending ? "Uploading…" : "Upload new photo"}
                  <input type="file" accept="image/*" className="hidden"
                         onChange={(e) => e.target.files?.[0] && uploadProfile.mutate(e.target.files[0])}/>
                </label>
                <p className="text-xs text-neutral-500 mt-2">JPG, PNG or WebP. Max 8 MB. Square photos look best.</p>
              </div>
            </div>
          </div>

          {/* Transformation gallery uploader */}
          <div className="card-fl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 font-bold"><ImageIcon size={16}/> Transformation gallery</div>
              <label className="btn-accent !py-1.5 !px-3 !text-xs cursor-pointer" data-testid="upload-gallery-btn">
                <Upload size={12}/> {uploadGallery.isPending ? "Uploading…" : "Add photo"}
                <input type="file" accept="image/*" className="hidden"
                       onChange={(e) => e.target.files?.[0] && uploadGallery.mutate(e.target.files[0])}/>
              </label>
            </div>
            <p className="text-xs text-neutral-500 mb-4">Show off client transformations, workspace, action shots. Up to 12 photos recommended.</p>
            {(form.gallery || []).length === 0 ? (
              <div className="border border-dashed border-neutral-200 rounded-lg p-8 text-center text-neutral-500 text-sm">
                No photos yet. Add your first transformation photo.
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {form.gallery.map((url) => {
                  const src = url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
                  const path = url.replace(/^.*\/api\/files\//, "");
                  return (
                    <div key={url} className="relative aspect-square rounded-lg overflow-hidden group bg-neutral-100">
                      <img src={src} alt="" className="w-full h-full object-cover"/>
                      <button data-testid={`gallery-delete-${path}`} onClick={() => deleteGallery.mutate(path)}
                              className="absolute top-2 right-2 rounded-full bg-white/95 p-1 opacity-0 group-hover:opacity-100 hover:bg-white transition">
                        <X size={14} className="text-neutral-900"/>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Basic profile fields */}
          <div className="card-fl p-6">
            <div className="font-bold mb-4">Basic info</div>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Display name" value={form.name || me.data?.name} onChange={(v) => setForm({...form, name: v})}/>
              <Field label="Phone" value={form.phone} onChange={(v) => setForm({...form, phone: v})}/>
              <Field label="County" value={form.county} onChange={(v) => setForm({...form, county: v})}/>
              <Field label="City" value={form.city} onChange={(v) => setForm({...form, city: v})}/>
              <Field label="Gender" value={form.gender} onChange={(v) => setForm({...form, gender: v})}/>
              <Field label="Hourly rate (€)" type="number" value={form.hourly_rate} onChange={(v) => setForm({...form, hourly_rate: Number(v)})}/>
              <Field label="Experience (years)" type="number" value={form.experience_years} onChange={(v) => setForm({...form, experience_years: Number(v)})}/>
              <div className="md:col-span-2">
                <label className="text-xs font-bold uppercase tracking-widest text-neutral-500">Bio</label>
                <textarea data-testid="profile-bio" value={form.bio || ""} onChange={(e) => setForm({...form, bio: e.target.value})} rows={4}
                          className="mt-1 w-full rounded-md border border-neutral-200 p-3 text-sm outline-none focus:border-neutral-900"/>
              </div>
              <Field label="Specialities (comma separated)" value={(form.specialities || []).join(", ")} onChange={(v) => setForm({...form, specialities: v.split(",").map(x => x.trim()).filter(Boolean)})}/>
              <Field label="Goals (comma separated)" value={(form.goals || []).join(", ")} onChange={(v) => setForm({...form, goals: v.split(",").map(x => x.trim()).filter(Boolean)})}/>
              <label className="flex items-center gap-2 text-sm">
                <input data-testid="profile-online" type="checkbox" checked={!!form.online_coaching} onChange={(e) => setForm({...form, online_coaching: e.target.checked})} className="accent-emerald-600"/>
                Offer online coaching
              </label>
              <div className="md:col-span-2">
                <button data-testid="profile-save" onClick={() => saveProfile.mutate()} className="btn-primary">Save profile</button>
              </div>
            </div>
          </div>

          <ServicesEditor services={form.services || []} />
          <BusinessSettings data={form} />
          <AvailabilityEditor availability={form.availability || {}} />
          <QualificationsEditor qualifications={form.qualifications || []} />
          <TrainerReviewsCard />
        </div>
      )}
    </div>
  );
}

function BusinessSettings({ data }) {
  const qc = useQueryClient();
  const [away, setAway] = useState(!!data.away);
  const [awayMsg, setAwayMsg] = useState(data.away_message || "");
  const [minNotice, setMinNotice] = useState(data.min_notice_hours || 0);
  const [rate, setRate] = useState(data.hourly_rate || 0);

  useEffect(() => {
    setAway(!!data.away);
    setAwayMsg(data.away_message || "");
    setMinNotice(data.min_notice_hours || 0);
    setRate(data.hourly_rate || 0);
  }, [data]);

  const slug = data.slug || data.id;
  const publicUrl = slug ? `/trainer/${slug}` : null;

  const saveAway = useMutation({
    mutationFn: (body) => api.put("/trainer/away", body),
    onSuccess: () => { toast.success(away ? "Vacation mode on — hidden from search" : "You're back online"); qc.invalidateQueries({ queryKey: ["trainer-me"] }); },
  });
  const saveNotice = useMutation({
    mutationFn: (body) => api.put("/trainer/min-notice", body),
    onSuccess: () => { toast.success("Minimum notice updated"); qc.invalidateQueries({ queryKey: ["trainer-me"] }); },
  });
  const saveRate = useMutation({
    mutationFn: (body) => api.put("/trainer/hourly-rate", body),
    onSuccess: () => { toast.success("Hourly rate updated"); qc.invalidateQueries({ queryKey: ["trainer-me"] }); },
  });

  return (
    <div className="card-fl p-6" data-testid="business-settings">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-bold">Business settings</div>
          <p className="text-xs text-neutral-500">Quick controls customers see on your profile.</p>
        </div>
        {publicUrl && (
          <a href={publicUrl} target="_blank" rel="noreferrer" data-testid="preview-profile-btn"
             className="btn-secondary !py-2 !px-3 !text-xs">
            View public profile ↗
          </a>
        )}
      </div>

      {/* Vacation mode */}
      <div className={`rounded-lg border p-4 mb-4 ${away ? "bg-amber-50 border-amber-200" : "border-neutral-200"}`}>
        <label className="flex items-center gap-3 text-sm font-semibold">
          <input data-testid="away-toggle" type="checkbox" checked={away}
                 onChange={(e) => setAway(e.target.checked)} className="accent-emerald-600 w-4 h-4"/>
          Vacation mode — hide my profile from search and pause new bookings
        </label>
        {away && (
          <input data-testid="away-message" value={awayMsg}
                 onChange={(e) => setAwayMsg(e.target.value)}
                 placeholder="Optional note shown if someone finds a direct link (e.g. 'Back on 5 Mar')"
                 className="mt-3 h-10 w-full rounded-md border border-amber-300 bg-white px-3 text-sm outline-none focus:border-amber-500" maxLength={300}/>
        )}
        <button data-testid="save-away" onClick={() => saveAway.mutate({ away, away_message: awayMsg })}
                className="btn-primary !py-1.5 !px-3 !text-xs mt-3">Save</button>
      </div>

      {/* Hourly rate quick-edit + min notice */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-bold uppercase tracking-widest text-neutral-500">Hourly rate (€) — shown on your profile</label>
          <div className="flex gap-2 mt-1">
            <input data-testid="quick-hourly-rate" type="number" min="0" value={rate}
                   onChange={(e) => setRate(Number(e.target.value))}
                   className="h-11 flex-1 rounded-md border border-neutral-200 px-3 text-sm outline-none focus:border-neutral-900"/>
            <button data-testid="save-hourly-rate" onClick={() => saveRate.mutate({ hourly_rate: rate })}
                    className="btn-primary !text-sm !py-2 !px-3">Save</button>
          </div>
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-widest text-neutral-500">Minimum booking notice (hours)</label>
          <div className="flex gap-2 mt-1">
            <input data-testid="min-notice-input" type="number" min="0" max="168" value={minNotice}
                   onChange={(e) => setMinNotice(Math.max(0, Math.min(168, Number(e.target.value) || 0)))}
                   className="h-11 flex-1 rounded-md border border-neutral-200 px-3 text-sm outline-none focus:border-neutral-900"/>
            <button data-testid="save-min-notice" onClick={() => saveNotice.mutate({ min_notice_hours: minNotice })}
                    className="btn-primary !text-sm !py-2 !px-3">Save</button>
          </div>
          <p className="text-[11px] text-neutral-400 mt-1">Customers can't book sessions sooner than this. 0 = no limit.</p>
        </div>
      </div>
    </div>
  );
}

function TrainerReviewsCard() {
  const qc = useQueryClient();
  const reviews = useQuery({
    queryKey: ["trainer-reviews-mine"],
    queryFn: async () => (await api.get("/trainer/reviews/mine")).data,
  });
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState("");

  const save = useMutation({
    mutationFn: ({ id, response }) => api.post(`/trainer/reviews/${id}/respond`, { response }),
    onSuccess: () => { toast.success("Response posted"); setEditingId(null); setDraft(""); qc.invalidateQueries({ queryKey: ["trainer-reviews-mine"] }); },
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/trainer/reviews/${id}/respond`),
    onSuccess: () => { toast.success("Response removed"); qc.invalidateQueries({ queryKey: ["trainer-reviews-mine"] }); },
  });

  const items = reviews.data || [];
  if (items.length === 0) {
    return (
      <div className="card-fl p-6" data-testid="trainer-reviews-card">
        <div className="font-bold mb-1">Reviews</div>
        <p className="text-sm text-neutral-500">You don't have any reviews yet. They'll show up here after your first completed and reviewed session.</p>
      </div>
    );
  }

  return (
    <div className="card-fl p-6" data-testid="trainer-reviews-card">
      <div className="font-bold mb-1">Reviews</div>
      <p className="text-xs text-neutral-500 mb-4">Your public reviews. Add a response — it shows under the review on your profile.</p>
      <div className="space-y-3">
        {items.map((r) => (
          <div key={r.id} className="border border-neutral-200 rounded-lg p-4 bg-white">
            <div className="flex items-center justify-between mb-1">
              <div className="text-sm font-semibold">{r.customer_name || "Customer"}</div>
              <div className="text-xs text-amber-600">{"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}</div>
            </div>
            <p className="text-sm text-neutral-700 leading-relaxed">{r.comment || <em className="text-neutral-400">No comment</em>}</p>
            {r.response ? (
              <div className="mt-3 p-3 rounded-md bg-emerald-50 border border-emerald-200 text-sm">
                <div className="text-xs font-bold text-emerald-700 mb-1">Your reply</div>
                <div className="text-neutral-800">{r.response}</div>
                <button data-testid={`delete-response-${r.id}`} onClick={() => del.mutate(r.id)}
                        className="text-xs text-red-600 mt-2 hover:underline">Remove reply</button>
              </div>
            ) : editingId === r.id ? (
              <div className="mt-3">
                <textarea data-testid={`response-textarea-${r.id}`} value={draft}
                          onChange={(e) => setDraft(e.target.value)} rows={3} maxLength={1000}
                          placeholder="Thank you for the kind words / here's some context..."
                          className="w-full rounded-md border border-neutral-200 p-2 text-sm outline-none focus:border-neutral-900"/>
                <div className="flex gap-2 mt-2">
                  <button data-testid={`post-response-${r.id}`} disabled={!draft.trim() || save.isPending}
                          onClick={() => save.mutate({ id: r.id, response: draft.trim() })}
                          className="btn-primary !text-xs !py-1.5 !px-3">Post reply</button>
                  <button onClick={() => { setEditingId(null); setDraft(""); }} className="btn-secondary !text-xs !py-1.5 !px-3">Cancel</button>
                </div>
              </div>
            ) : (
              <button data-testid={`reply-${r.id}`} onClick={() => { setEditingId(r.id); setDraft(""); }}
                      className="text-xs text-emerald-700 mt-2 hover:underline font-semibold">Reply publicly</button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const Stat = ({ label, value, icon, accent }) => (
  <div className={`card-fl p-5 ${accent ? "!bg-neutral-950 !border-neutral-950" : ""}`}>
    <div className={`text-xs font-semibold uppercase tracking-widest flex items-center gap-1.5 ${accent ? "text-emerald-400" : "text-neutral-500"}`}>{icon} {label}</div>
    <div className={`text-2xl font-black mt-2 ${accent ? "text-emerald-400" : ""}`}>{value}</div>
  </div>
);

function EarningsTab({ statsData }) {
  const earnings = useQuery({
    queryKey: ["trainer-earnings"],
    queryFn: async () => (await api.get("/trainer/earnings")).data,
  });
  const rows = earnings.data || [];

  const exportCsv = () => {
    const header = ["Paid at", "Date", "Slot", "Customer", "Service", "You earned (€)", "Status"];
    const lines = rows.map((b) => [
      (b.paid_at || "").split("T")[0],
      b.date,
      b.slot,
      (b.customer_name || "").replace(/,/g, " "),
      (b.service_title || "").replace(/,/g, " "),
      Number(b.trainer_net || 0).toFixed(2),
      b.status,
    ].join(","));
    const csv = [header.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `fitlink-earnings-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="earnings-tab">
      <div className="card-fl p-6 mb-6 !bg-neutral-950 !border-neutral-950">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400 mb-2">Total earnings</div>
        <div className="text-4xl font-black text-emerald-400">€{(statsData?.net_earnings || 0).toFixed(2)}</div>
        <div className="text-sm text-neutral-400 mt-2">Across {statsData?.paid_bookings || 0} paid bookings</div>
      </div>

      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold">Paid bookings ({rows.length})</h2>
        <button data-testid="export-csv" onClick={exportCsv} disabled={rows.length === 0} className="btn-secondary">
          <Download size={14}/> Export CSV
        </button>
      </div>

      {rows.length === 0 ? (
        <div className="border border-dashed border-neutral-200 rounded-xl p-10 text-center text-neutral-500">
          No paid bookings yet. Earnings will appear here once customers pay.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-neutral-200 bg-white">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-widest text-neutral-500 bg-neutral-50">
              <tr>
                <th className="text-left px-4 py-3">Paid</th>
                <th className="text-left px-4 py-3">Session</th>
                <th className="text-left px-4 py-3">Customer</th>
                <th className="text-right px-4 py-3">You earned</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((b) => (
                <tr key={b.id} className="border-t border-neutral-100">
                  <td className="px-4 py-3 text-neutral-500">{(b.paid_at || "").split("T")[0]}</td>
                  <td className="px-4 py-3">{b.service_title} <span className="text-neutral-400 text-xs">· {b.date} {b.slot}</span></td>
                  <td className="px-4 py-3">{b.customer_name}</td>
                  <td className="px-4 py-3 text-right font-bold text-emerald-700">€{Number(b.trainer_net || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, value, onChange, type = "text" }) => (
  <div>
    <label className="text-xs font-bold uppercase tracking-widest text-neutral-500">{label}</label>
    <input data-testid={`field-${label.replace(/\s+/g,'-').toLowerCase()}`} type={type} value={value || ""} onChange={(e) => onChange(e.target.value)}
           className="mt-1 h-11 w-full rounded-md border border-neutral-200 px-3 text-sm outline-none focus:border-neutral-900"/>
  </div>
);

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const TIME_SLOTS = ["06:00", "07:00", "08:00", "09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00", "17:00", "18:00", "19:00", "20:00", "21:00"];

function ServicesEditor({ services }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState({ title: "", price: "", duration: "60 min" });
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ title: "", price: "", duration: "" });

  const add = useMutation({
    mutationFn: (body) => api.post("/trainer/services", body),
    onSuccess: () => { toast.success("Service added"); setDraft({ title: "", price: "", duration: "60 min" }); qc.invalidateQueries({ queryKey: ["trainer-me"] }); },
  });
  const update = useMutation({
    mutationFn: (body) => api.put("/trainer/services", body),
    onSuccess: () => { toast.success("Service updated"); setEditingId(null); qc.invalidateQueries({ queryKey: ["trainer-me"] }); },
  });
  const del = useMutation({
    mutationFn: (id) => api.post("/trainer/services/delete", { id }),
    onSuccess: () => { toast.success("Service removed"); qc.invalidateQueries({ queryKey: ["trainer-me"] }); },
  });

  const canAdd = draft.title.trim() && Number(draft.price) >= 0 && draft.duration.trim();
  const canSaveEdit = editDraft.title.trim() && Number(editDraft.price) >= 0 && editDraft.duration.trim();

  const startEdit = (s) => {
    setEditingId(s.id);
    setEditDraft({ title: s.title, price: String(s.price), duration: s.duration });
  };

  return (
    <div className="card-fl p-6" data-testid="services-editor">
      <div className="font-bold mb-1">Services & pricing</div>
      <p className="text-xs text-neutral-500 mb-4">Add every service you offer so customers can book them directly. Change prices anytime.</p>
      <div className="space-y-2 mb-4">
        {services.length === 0 && (
          <div className="border border-dashed border-neutral-200 rounded-lg p-6 text-sm text-neutral-500 text-center">
            No services yet. Add your first one below.
          </div>
        )}
        {services.map((s) => (
          <div key={s.id || s.title} className="border border-neutral-200 rounded-lg px-4 py-3 bg-white">
            {editingId === s.id ? (
              <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_1fr_auto_auto] gap-2 items-end" data-testid={`edit-service-${s.id}`}>
                <div>
                  <label className="text-xs font-bold uppercase tracking-widest text-neutral-500">Service name</label>
                  <input data-testid={`edit-service-title-${s.id}`} value={editDraft.title} onChange={(e) => setEditDraft({ ...editDraft, title: e.target.value })}
                         className="mt-1 h-11 w-full rounded-md border border-neutral-200 px-3 text-sm outline-none focus:border-neutral-900"/>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-widest text-neutral-500">Price (€)</label>
                  <input data-testid={`edit-service-price-${s.id}`} type="number" min="0" value={editDraft.price} onChange={(e) => setEditDraft({ ...editDraft, price: e.target.value })}
                         className="mt-1 h-11 w-full rounded-md border border-neutral-200 px-3 text-sm outline-none focus:border-neutral-900"/>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-widest text-neutral-500">Duration</label>
                  <input data-testid={`edit-service-duration-${s.id}`} value={editDraft.duration} onChange={(e) => setEditDraft({ ...editDraft, duration: e.target.value })}
                         className="mt-1 h-11 w-full rounded-md border border-neutral-200 px-3 text-sm outline-none focus:border-neutral-900"/>
                </div>
                <button data-testid={`save-service-${s.id}`} disabled={!canSaveEdit || update.isPending}
                        onClick={() => update.mutate({ id: s.id, title: editDraft.title.trim(), price: Number(editDraft.price), duration: editDraft.duration.trim() })}
                        className="btn-primary !py-2 !px-3 !text-xs">Save</button>
                <button data-testid={`cancel-service-${s.id}`} onClick={() => setEditingId(null)} className="btn-secondary !py-2 !px-3 !text-xs">Cancel</button>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold text-sm">{s.title}</div>
                  <div className="text-xs text-neutral-500">{s.duration} · €{s.price}</div>
                </div>
                {s.id && (
                  <div className="flex gap-2">
                    <button data-testid={`edit-service-btn-${s.id}`} onClick={() => startEdit(s)}
                            className="btn-secondary !py-1 !px-2 !text-xs"><Pencil size={12}/> Edit</button>
                    <button data-testid={`delete-service-${s.id}`} onClick={() => del.mutate(s.id)} className="btn-secondary !py-1 !px-2 !text-xs">
                      <X size={12}/> Remove
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_1fr_auto] gap-2 items-end">
        <div>
          <label className="text-xs font-bold uppercase tracking-widest text-neutral-500">Service name</label>
          <input data-testid="new-service-title" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                 placeholder="e.g. 1-to-1 Personal Training" className="mt-1 h-11 w-full rounded-md border border-neutral-200 px-3 text-sm outline-none focus:border-neutral-900"/>
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-widest text-neutral-500">Price (€)</label>
          <input data-testid="new-service-price" type="number" min="0" value={draft.price} onChange={(e) => setDraft({ ...draft, price: e.target.value })}
                 className="mt-1 h-11 w-full rounded-md border border-neutral-200 px-3 text-sm outline-none focus:border-neutral-900"/>
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-widest text-neutral-500">Duration</label>
          <input data-testid="new-service-duration" value={draft.duration} onChange={(e) => setDraft({ ...draft, duration: e.target.value })}
                 className="mt-1 h-11 w-full rounded-md border border-neutral-200 px-3 text-sm outline-none focus:border-neutral-900"/>
        </div>
        <button data-testid="add-service-btn" disabled={!canAdd || add.isPending}
                onClick={() => add.mutate({ title: draft.title.trim(), price: Number(draft.price), duration: draft.duration.trim() })}
                className="btn-primary">Add</button>
      </div>
    </div>
  );
}

function AvailabilityEditor({ availability }) {
  const qc = useQueryClient();
  const [grid, setGrid] = useState(() => {
    const g = {};
    DAYS.forEach((d) => { g[d] = new Set(availability[d] || []); });
    return g;
  });

  useEffect(() => {
    const g = {};
    DAYS.forEach((d) => { g[d] = new Set(availability[d] || []); });
    setGrid(g);
  }, [availability]);

  const toggle = (day, slot) => {
    setGrid((prev) => {
      const next = { ...prev, [day]: new Set(prev[day]) };
      if (next[day].has(slot)) next[day].delete(slot); else next[day].add(slot);
      return next;
    });
  };

  const save = useMutation({
    mutationFn: () => {
      const payload = {};
      DAYS.forEach((d) => { payload[d] = Array.from(grid[d]).sort(); });
      return api.put("/trainer/availability", { availability: payload });
    },
    onSuccess: () => { toast.success("Availability saved"); qc.invalidateQueries({ queryKey: ["trainer-me"] }); },
  });

  return (
    <div className="card-fl p-6" data-testid="availability-editor">
      <div className="flex items-center justify-between mb-1">
        <div className="font-bold">Weekly availability</div>
        <button data-testid="save-availability" onClick={() => save.mutate()} disabled={save.isPending} className="btn-primary !py-1.5 !px-3 !text-xs">
          {save.isPending ? "Saving…" : "Save availability"}
        </button>
      </div>
      <p className="text-xs text-neutral-500 mb-4">Click any time slot to toggle it. Customers can only book the slots you've marked green.</p>
      <div className="overflow-x-auto">
        <div className="grid grid-cols-[80px_repeat(7,minmax(0,1fr))] gap-1 text-xs min-w-[520px]">
          <div></div>
          {DAYS.map((d) => (
            <div key={d} className="text-center font-bold text-neutral-900 py-1">{d}</div>
          ))}
          {TIME_SLOTS.map((slot) => (
            <React.Fragment key={slot}>
              <div className="text-neutral-500 text-right pr-2 py-1 font-mono">{slot}</div>
              {DAYS.map((d) => {
                const on = grid[d]?.has(slot);
                return (
                  <button key={d + slot} data-testid={`slot-${d}-${slot}`} onClick={() => toggle(d, slot)}
                          className={`h-8 rounded transition-colors ${on ? "bg-emerald-500 hover:bg-emerald-600" : "bg-neutral-100 hover:bg-neutral-200"}`}/>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function QualificationsEditor({ qualifications }) {
  const qc = useQueryClient();
  const [meta, setMeta] = useState({ title: "", issuer: "", year: "" });
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ title: "", issuer: "", year: "" });

  const upload = useMutation({
    mutationFn: async (file) => {
      const fd = new FormData();
      fd.append("file", file);
      const url = `/trainer/upload/qualification?title=${encodeURIComponent(meta.title)}&issuer=${encodeURIComponent(meta.issuer)}&year=${encodeURIComponent(meta.year)}`;
      const { data } = await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      return data;
    },
    onSuccess: () => { toast.success("Qualification uploaded"); setMeta({ title: "", issuer: "", year: "" }); qc.invalidateQueries({ queryKey: ["trainer-me"] }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Upload failed"),
  });
  const update = useMutation({
    mutationFn: (body) => api.put("/trainer/qualification", body),
    onSuccess: () => { toast.success("Qualification updated"); setEditingId(null); qc.invalidateQueries({ queryKey: ["trainer-me"] }); },
  });
  const del = useMutation({
    mutationFn: (id) => api.post("/trainer/qualification/delete", { qualification_id: id }),
    onSuccess: () => { toast.success("Removed"); qc.invalidateQueries({ queryKey: ["trainer-me"] }); },
  });

  const startEdit = (q) => {
    setEditingId(q.id);
    setEditDraft({ title: q.title || "", issuer: q.issuer || "", year: q.year ? String(q.year) : "" });
  };

  return (
    <div className="card-fl p-6" data-testid="qualifications-editor">
      <div className="font-bold mb-1">Qualifications & certificates</div>
      <p className="text-xs text-neutral-500 mb-4">Upload PDFs or images of your certificates. Admin reviews these before approving you.</p>
      <div className="space-y-2 mb-4">
        {qualifications.length === 0 && (
          <div className="border border-dashed border-neutral-200 rounded-lg p-6 text-sm text-neutral-500 text-center">
            No qualifications yet. Add your NCEHS / REPS / other certificates below.
          </div>
        )}
        {qualifications.map((q, i) => (
          <div key={q.id || i} className="border border-neutral-200 rounded-lg px-4 py-3 bg-white">
            {editingId === q.id ? (
              <div className="grid sm:grid-cols-[2fr_1fr_80px_auto_auto] gap-2 items-end" data-testid={`edit-qual-${q.id}`}>
                <input data-testid={`edit-qual-title-${q.id}`} value={editDraft.title}
                       onChange={(e) => setEditDraft({ ...editDraft, title: e.target.value })} placeholder="Title"
                       className="h-10 rounded-md border border-neutral-200 px-3 text-sm outline-none focus:border-neutral-900"/>
                <input data-testid={`edit-qual-issuer-${q.id}`} value={editDraft.issuer}
                       onChange={(e) => setEditDraft({ ...editDraft, issuer: e.target.value })} placeholder="Issuer"
                       className="h-10 rounded-md border border-neutral-200 px-3 text-sm outline-none focus:border-neutral-900"/>
                <input data-testid={`edit-qual-year-${q.id}`} value={editDraft.year}
                       onChange={(e) => setEditDraft({ ...editDraft, year: e.target.value })} placeholder="Year"
                       className="h-10 rounded-md border border-neutral-200 px-3 text-sm outline-none focus:border-neutral-900"/>
                <button data-testid={`save-qual-${q.id}`} disabled={!editDraft.title.trim() || update.isPending}
                        onClick={() => update.mutate({
                          id: q.id, title: editDraft.title.trim(), issuer: editDraft.issuer.trim(),
                          year: editDraft.year ? Number(editDraft.year) : null,
                        })}
                        className="btn-primary !py-2 !px-3 !text-xs">Save</button>
                <button onClick={() => setEditingId(null)} className="btn-secondary !py-2 !px-3 !text-xs">Cancel</button>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold text-sm">{q.title}</div>
                  <div className="text-xs text-neutral-500">
                    {[q.issuer, q.year].filter(Boolean).join(" · ") || "—"}
                    {q.file_url && (
                      <> · <a target="_blank" rel="noreferrer" className="text-emerald-700 underline"
                              href={q.file_url.startsWith("http") ? q.file_url : `${BACKEND}${q.file_url}`}>View file</a></>
                    )}
                  </div>
                </div>
                {q.id && (
                  <div className="flex gap-2">
                    <button data-testid={`edit-qual-btn-${q.id}`} onClick={() => startEdit(q)}
                            className="btn-secondary !py-1 !px-2 !text-xs"><Pencil size={12}/> Edit</button>
                    <button data-testid={`delete-qual-${q.id}`} onClick={() => del.mutate(q.id)} className="btn-secondary !py-1 !px-2 !text-xs">
                      <X size={12}/> Remove
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <input data-testid="qual-title" value={meta.title} onChange={(e) => setMeta({ ...meta, title: e.target.value })}
               placeholder="Title (e.g. NCEHS L3)" className="h-11 rounded-md border border-neutral-200 px-3 text-sm outline-none focus:border-neutral-900"/>
        <input data-testid="qual-issuer" value={meta.issuer} onChange={(e) => setMeta({ ...meta, issuer: e.target.value })}
               placeholder="Issuer" className="h-11 rounded-md border border-neutral-200 px-3 text-sm outline-none focus:border-neutral-900"/>
        <input data-testid="qual-year" value={meta.year} onChange={(e) => setMeta({ ...meta, year: e.target.value })}
               placeholder="Year" className="h-11 rounded-md border border-neutral-200 px-3 text-sm outline-none focus:border-neutral-900"/>
      </div>
      <label className="btn-accent mt-3 inline-flex cursor-pointer" data-testid="upload-qual-btn">
        <Upload size={14}/> {upload.isPending ? "Uploading…" : "Upload certificate (PDF or image)"}
        <input type="file" accept="application/pdf,image/*" className="hidden"
               onChange={(e) => e.target.files?.[0] && upload.mutate(e.target.files[0])}/>
      </label>
    </div>
  );
}
